import { TriangleAlertIcon } from 'lucide-react'

import { LiveFeedbackPanel } from '@/components'
import { useToast } from '@/hooks'
import {
  type Analysis,
  type AnalysisPayload,
  AnalysisSkeleton,
  GenerationProgressPanel,
  hasAnalysisFailed,
  hasAnalysisGenerated,
  isAnalysisGenerating,
  useAnalysisMutating,
  useAnalysisMutations,
} from '@/modules/analysis'
import {
  type Project,
  useCaseConfigIsLoading,
  useLoadingProject,
  useTransactions,
} from '@/modules/projects'
import GenerateReport from './generate/GenerateReport'

interface AnalysisGenerateSectionProps {
  project: Project
  analysis: Analysis
}

const PHRASES = [
  'Reviewing the financial record...',
  'Cross-referencing transactions...',
  'Following the money trail...',
  'Deposing the bank statements...',
  'Examining the ledger for irregularities...',
  'Interviewing the numbers...',
  'Subpoenaing the spreadsheets...',
  'Building the evidentiary foundation...',
  'Establishing a pattern of conduct...',
  'Preparing exhibit A through Z...',
  'Compiling the forensic dossier...',
  'Running a financial background check...',
  'Auditing every last cent...',
  'Tracing funds through shell accounts...',
  'Checking for undisclosed liabilities...',
  'Reconciling the paper trail...',
  'Flagging suspicious disbursements...',
  'Scrutinizing the cash flow narrative...',
  'Corroborating financial testimony...',
  'Drafting findings of fact...',
]

export default function AnalysisGenerateSection({
  project,
  analysis,
}: AnalysisGenerateSectionProps) {
  const { successToast, errorToast } = useToast()
  const { createAnalysis } = useAnalysisMutations()
  const isProjectLoading = useLoadingProject()
  const isMutating = useAnalysisMutating()
  const isLoadingConfigOptions = useCaseConfigIsLoading()
  const transactions = useTransactions()

  if (hasAnalysisGenerated(analysis)) return null

  if (isMutating || isAnalysisGenerating(analysis)) {
    if (analysis.progress) {
      return <GenerationProgressPanel progress={analysis.progress} />
    }
    return <LiveFeedbackPanel phrases={PHRASES} />
  }

  if (isLoadingConfigOptions || isProjectLoading) {
    return <AnalysisSkeleton />
  }

  const handleSubmit = (payload: AnalysisPayload) => {
    createAnalysis(project.slug, payload)
      .then(() => successToast('Report analysis started'))
      .catch((error) => errorToast('Failed to generate report', error))
  }

  return (
    <>
      {hasAnalysisFailed(analysis) && (
        <div className="max-w-3xl mx-auto mb-6 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <TriangleAlertIcon className="h-4 w-4 mt-0.5 text-red-700 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-red-900">
              The last report generation did not finish
            </p>
            <p className="text-red-800">
              Adjust the setup if needed and generate the report again.
            </p>
          </div>
        </div>
      )}
      <GenerateReport
        key={project.slug}
        project={project}
        analysis={analysis}
        isSubmitting={isMutating}
        onSubmit={handleSubmit}
        transactionCount={transactions.length}
      />
    </>
  )
}
